import type { UserRolePermissions } from '@/services/userRoleService';
import type { PermissionState } from './hooks';

/** Maps the flat permission state into the API payload shape. */
export const buildPermissionsPayload = (
  permissions: PermissionState,
  isAdministrator: boolean,
): UserRolePermissions => ({
  permissions: Array.from(permissions.enabledPermissions),
  timesheetAccess: {
    viewAccess: permissions.viewTimesheetsAccess as 'none' | 'own' | 'team' | 'all',
    editAccess: permissions.editTimesheetsAccess as 'none' | 'own' | 'team' | 'all',
    allowDelete: permissions.allowDeleteTimesheets,
    allowDuplicate: permissions.allowDuplicateTimesheets,
    allowRecall: permissions.allowRecallTimesheet,
  },
  smartFeaturesPermissions: {
    enableCopyFromLastWeek: permissions.enableCopyFromLastWeek,
    enableCopyDescription: permissions.enableCopyDescription,
    enableCopyOnlyBillable: permissions.enableCopyOnlyBillable,
    enableAutoSmartFill: permissions.enableAutoSmartFill,
    enableAiInsights: permissions.enableAiInsights,
  },
  approvalPermissions: { viewDashboard: permissions.viewApprovalDashboard },
  timesheetApprovalPermissions: {
    viewApprovals: permissions.viewApprovals,
    editTimesheets: permissions.editTimesheetsApproval,
    rejectTimesheets: permissions.rejectTimesheets,
    compulsoryRejectionReason: permissions.compulsoryRejectionReason,
    bulkApprove: permissions.bulkApprove,
    delegateApproval: permissions.delegateApproval,
    viewPolicyViolations: permissions.viewPolicyViolations,
    viewDetectedIssues: permissions.viewDetectedIssues,
  },
  reportAccessPermissions: {
    timesheetReport: { enabled: permissions.enableTimesheetReport, viewAccess: permissions.viewTimesheetReportAccess, allowExport: permissions.allowTimesheetReportExport },
    missingTimesheetReport: { enabled: permissions.enableMissingTimesheetReport, viewAccess: permissions.viewMissingTimesheetReportAccess, allowExport: permissions.allowMissingTimesheetReportExport },
    utilisationReport: { enabled: permissions.enableUtilisationReport, viewAccess: permissions.viewUtilisationReportAccess, allowExport: permissions.allowUtilisationReportExport },
    // billableHoursReport removed — belongs in the Projects module of the PSA suite.
  },
  employeeDirectoryPermissions: { viewDirectory: permissions.viewEmployeeDirectory, viewAccess: permissions.viewEmployeeDirectoryAccess, viewDetails: permissions.viewEmployeeDetails },
  integrationsPermissions: { viewIntegrations: permissions.viewIntegrations, manageIntegrations: permissions.manageIntegrations },
  companyManagementPermissions: { viewCompanies: permissions.viewCompanies, manageCompanies: permissions.manageCompanies },
  systemSettingsPermissions: {
    manageGeneralSettings: permissions.manageGeneralSettings,
    manageDeadlines: permissions.manageDeadlines,
    managePolicySettings: permissions.managePolicySettings,
    manageUtilisationSettings: permissions.manageUtilisationSettings,
    manageTimeEntrySettings: permissions.manageTimeEntrySettings,
    manageWorkTypeSettings: permissions.manageWorkTypeSettings,
    manageApprovalsSettings: permissions.manageApprovalsSettings,
    manageNotifications: permissions.manageNotifications,
    manageRolesPermissions: isAdministrator ? true : permissions.manageRolesPermissions,
    manageRates: permissions.manageRates,
    manageScreenDataModes: permissions.manageScreenDataModes,
  },
});
